/**
 * @fileoverview Syndicator Showdown — useBlockingModalOpen
 * @module hooks/useBlockingModalOpen
 *
 * The app has no shared ModalContext, so this hook detects "is a gameplay
 * modal currently open" via a lightweight DOM watch for known overlay root
 * classes. Used by NotificationToastHost so premium notification cards never
 * render on top of BuyModal, the Stable Slip (ReviewSlip) or the Share QR
 * modal — those overlays are portalled directly under <body>, so a
 * MutationObserver on document.body reliably catches them mount/unmount.
 */

import { useEffect, useState } from 'react';

// Root overlay classes for modals that must never be covered by a toast.
const BLOCKING_SELECTORS = [
  '.buy-modal__overlay',
  '.stable-slip__overlay',
  '.sqm-overlay',
].join(', ');

function isBlocked() {
  if (typeof document === 'undefined') return false;
  return !!document.querySelector(BLOCKING_SELECTORS);
}

export function useBlockingModalOpen() {
  const [blocked, setBlocked] = useState(isBlocked);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') {
      return undefined;
    }

    const recheck = () => setBlocked(isBlocked());
    recheck();

    const observer = new MutationObserver(recheck);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return blocked;
}

export default useBlockingModalOpen;
